import { useState } from 'react';
import { ChevronLeft, ChevronRight, Camera, MapPin, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useIsBelowLg } from '@/hooks/use-mobile';

interface ProjectImageGalleryProps {
  images: any[];
  name: string;
  developerLogo?: string | null;
  developerName?: string;
}

export function ProjectImageGallery({ images, name, developerLogo, developerName }: ProjectImageGalleryProps) {
  const [selectedImage, setSelectedImage] = useState(0);
  const isMobile = useIsBelowLg();
  const mainImg = images[selectedImage]?.url || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=600&fit=crop';
  const sideImages = images.slice(0, 4);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
  };

  if (isMobile) {
    return (
      <div className="-mx-4 mb-4">
        <div className="relative aspect-[4/3] bg-muted overflow-hidden">
          <img src={mainImg} alt={name} className="w-full h-full object-cover" />

          {developerName && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-md px-2.5 py-1 flex items-center gap-1.5 text-[11px] font-medium shadow-sm">
              {developerLogo && <img src={developerLogo} alt="" className="h-4 w-4 rounded object-cover" />}
              <span>By {developerName}</span>
            </div>
          )}

          <button
            onClick={handleShare}
            className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full p-1.5 shadow-sm"
          >
            <Share2 className="h-4 w-4 text-foreground" />
          </button>

          {images.length > 1 && (
            <>
              <button
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full h-8 w-8 flex items-center justify-center shadow-sm"
                onClick={() => setSelectedImage(i => (i - 1 + images.length) % images.length)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full h-8 w-8 flex items-center justify-center shadow-sm"
                onClick={() => setSelectedImage(i => (i + 1) % images.length)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Photo count badge */}
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-1 rounded-md flex items-center gap-1">
            <Camera className="h-3 w-3" /> {selectedImage + 1}/{images.length}
          </div>

          {/* Dots indicator */}
          {images.length > 1 && images.length <= 8 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`h-1.5 rounded-full transition-all ${i === selectedImage ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 rounded-xl overflow-hidden">
        {/* Main Image */}
        <div className="lg:col-span-2 relative aspect-[16/10] bg-muted group">
          <img src={mainImg} alt={name} className="w-full h-full object-cover" />
          {developerName && (
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs font-medium shadow-sm">
              {developerLogo && <img src={developerLogo} alt="" className="h-5 w-5 rounded object-cover" />}
              <span>By {developerName}</span>
            </div>
          )}
          <button
            onClick={handleShare}
            className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-sm hover:bg-white transition-colors"
          >
            <Share2 className="h-4 w-4 text-foreground" />
          </button>
          {images.length > 1 && (
            <>
              <Button variant="ghost" size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full h-9 w-9 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => setSelectedImage(i => (i - 1 + images.length) % images.length)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full h-9 w-9 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => setSelectedImage(i => (i + 1) % images.length)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}
        </div>
        {/* Side Thumbnails */}
        <div className="hidden lg:grid grid-rows-3 gap-2">
          {sideImages.slice(1, 4).map((img: any, i: number) => (
            <button
              key={img.id}
              onClick={() => setSelectedImage(i + 1)}
              className={`relative overflow-hidden bg-muted rounded-lg ${i + 1 === selectedImage ? 'ring-2 ring-primary' : 'hover:opacity-90'}`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
              {i === 2 && images.length > 4 && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-4">
                  <div className="flex items-center gap-1 text-white text-sm font-medium">
                    <Camera className="h-4 w-4" /> {images.length}
                  </div>
                  <div className="flex items-center gap-1 text-white text-sm font-medium">
                    <MapPin className="h-4 w-4" /> Map
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
